import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { shopApi } from '../../api/shop';
import { useAuth } from '../../auth/AuthProvider';
import { config } from '../../config';
import { RequireShop } from '../../components/ShopSelector';
import { Breadcrumb, SectionCard } from '../../components/PageChrome';
import { ErrorBanner, LoadingState } from '../../components/Layout';
import { useShop } from '../../context/ShopContext';
import { formatMoney, mediaUrl, slugify } from '../../utils/format';

function emptyVariant(currency = 'USD') {
  return {
    localKey: crypto.randomUUID(),
    id: null,
    name: '',
    sku: '',
    price: '',
    cost_per_unit: '',
    currency,
    track_inventory: true,
    image_id: null,
    imageUrl: null,
  };
}

function variantFromApi(variant) {
  return {
    localKey: String(variant.id),
    id: variant.id,
    name: variant.name || '',
    sku: variant.sku || '',
    price: String(variant.price),
    cost_per_unit: String(variant.cost_per_unit),
    currency: variant.currency || 'USD',
    track_inventory: variant.track_inventory,
    image_id: variant.image_id,
    imageUrl: mediaUrl(variant.image?.image, config.shopApiUrl),
  };
}

function variantPayload(row) {
  const price = Number(row.price);
  return {
    name: row.name.trim() || null,
    sku: row.sku.trim() || null,
    price,
    cost_per_unit: Number(row.cost_per_unit || price),
    currency: row.currency,
    track_inventory: row.track_inventory,
  };
}

function productPayload(form) {
  return {
    name: form.name.trim(),
    slug: form.slug.trim(),
    summary: form.summary.trim() || null,
    description: form.description.trim() || null,
    meta_title: form.meta_title.trim() || null,
    meta_description: form.meta_description.trim() || null,
    category_id: Number(form.category_id),
    brand: form.brand.trim() || null,
    is_live: form.is_live,
    status: form.is_live ? 'PUBLISHED' : 'DRAFT',
    collection_ids: form.collection_ids,
    tags: form.tags,
  };
}

function TagEditor({ tags, onChange, suggestions = [] }) {
  const [input, setInput] = useState('');

  const addTag = (raw) => {
    const name = raw.trim().replace(/,/g, '');
    if (!name) return;
    if (tags.some((tag) => tag.toLowerCase() === name.toLowerCase())) return;
    onChange([...tags, name]);
    setInput('');
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      addTag(input);
    }
    if (event.key === 'Backspace' && !input && tags.length) {
      onChange(tags.slice(0, -1));
    }
  };

  return (
    <div className="tag-editor">
      <div className="tag-list">
        {tags.map((tag) => (
          <span key={tag} className="tag-chip">
            {tag}
            <button type="button" onClick={() => onChange(tags.filter((item) => item !== tag))}>
              ×
            </button>
          </span>
        ))}
        <input
          className="tag-input"
          value={input}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => addTag(input)}
          placeholder={tags.length ? '' : 'Vintage, cotton, summer'}
        />
      </div>
      {suggestions.length > 0 && (
        <div className="tag-suggestions">
          {suggestions
            .filter((item) => !tags.some((tag) => tag.toLowerCase() === item.name.toLowerCase()))
            .slice(0, 8)
            .map((item) => (
              <button key={item.id} type="button" className="tag-suggestion" onClick={() => addTag(item.name)}>
                {item.name}
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

function SeoPreview({ title, description, slug, productTitle }) {
  return (
    <div className="seo-preview">
      <div className="seo-preview-title">{title || productTitle || 'Product title'}</div>
      <div className="seo-preview-url">
        yourstore.com › products › {slug || 'product-handle'}
      </div>
      <div className="seo-preview-desc">
        {description || 'Add a meta description to improve search appearance.'}
      </div>
    </div>
  );
}

export default function ProductEditPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { selectedShopId } = useShop();
  const isNew = productId === 'new';

  const [product, setProduct] = useState(null);
  const [categories, setCategories] = useState([]);
  const [collections, setCollections] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [images, setImages] = useState([]);
  const [variants, setVariants] = useState([emptyVariant()]);
  const [defaultVariantId, setDefaultVariantId] = useState(null);
  const [form, setForm] = useState({
    name: '',
    slug: '',
    summary: '',
    description: '',
    meta_title: '',
    meta_description: '',
    category_id: '',
    product_type_id: '',
    brand: '',
    is_live: false,
    currency: 'USD',
    collection_ids: [],
    tags: [],
  });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [variantBusy, setVariantBusy] = useState(null);
  const productImageInputRef = useRef(null);
  const variantImageInputRef = useRef(null);
  const [pendingVariantImageKey, setPendingVariantImageKey] = useState(null);

  const loadProduct = useCallback(async () => {
    if (!selectedShopId || isNew || !/^\d+$/.test(productId)) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const [productData, categoryData] = await Promise.all([
        shopApi.getProduct(token, selectedShopId, productId),
        shopApi.listCategories(token, selectedShopId),
      ]);
      setProduct(productData);
      setCategories(categoryData);
      setImages(productData.images || []);
      setDefaultVariantId(productData.default_variant_id);
      setVariants(
        productData.variants?.length
          ? productData.variants.map(variantFromApi)
          : [emptyVariant()],
      );
      const defaultVariant =
        productData.variants?.find((v) => v.id === productData.default_variant_id) ||
        productData.variants?.[0];
      setForm({
        name: productData.name,
        slug: productData.slug,
        summary: productData.summary || '',
        description: productData.description || '',
        meta_title: productData.meta_title || '',
        meta_description: productData.meta_description || '',
        category_id: String(productData.category_id),
        product_type_id: String(productData.product_type_id),
        brand: productData.brand || '',
        is_live: productData.is_live,
        currency: defaultVariant?.currency || 'USD',
        collection_ids: (productData.collections || []).map((item) => item.id),
        tags: (productData.tags || []).map((item) => item.name),
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken, isNew, productId, selectedShopId]);

  const loadMeta = useCallback(async () => {
    if (!selectedShopId) return;
    try {
      const token = await getToken();
      const [categoryData, collectionData, tagData, typeData] = await Promise.all([
        shopApi.listCategories(token, selectedShopId),
        shopApi.listCollections(token, selectedShopId),
        shopApi.listTags(token, selectedShopId),
        shopApi.listProductTypes(token, selectedShopId),
      ]);
      setCategories(categoryData);
      setCollections(collectionData);
      setAllTags(tagData);
      setProductTypes(typeData);
      if (isNew && typeData.length === 1) {
        setForm((prev) => ({ ...prev, product_type_id: String(typeData[0].id) }));
      }
    } catch (err) {
      setError(err.message);
    }
  }, [getToken, isNew, selectedShopId]);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  useEffect(() => {
    loadProduct();
  }, [loadProduct]);

  const handleNameChange = (name) => {
    setForm((prev) => ({
      ...prev,
      name,
      slug: isNew && !prev.slug ? slugify(name) : prev.slug,
      meta_title: prev.meta_title || name,
    }));
  };

  const toggleCollection = (collectionId) => {
    setForm((prev) => ({
      ...prev,
      collection_ids: prev.collection_ids.includes(collectionId)
        ? prev.collection_ids.filter((id) => id !== collectionId)
        : [...prev.collection_ids, collectionId],
    }));
  };

  const refreshProduct = async (token) => {
    const updated = await shopApi.getProduct(token, selectedShopId, product.id);
    setProduct(updated);
    setImages(updated.images || []);
    setVariants(
      updated.variants?.length ? updated.variants.map(variantFromApi) : [emptyVariant()],
    );
    setDefaultVariantId(updated.default_variant_id);
    return updated;
  };

  const handleSaveProduct = async (event) => {
    event.preventDefault();
    if (!form.category_id || (!isNew && !form.product_type_id)) {
      setError('Select a category.');
      return;
    }
    if (isNew && !form.product_type_id) {
      setError('Select a product type.');
      return;
    }
    if (!variants.length || !variants[0].price) {
      setError('Add at least one variant with a price.');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const token = await getToken();

      if (isNew) {
        const first = variants[0];
        const created = await shopApi.createProduct(token, selectedShopId, {
          ...productPayload(form),
          product_type_id: Number(form.product_type_id),
          default_variant: {
            ...variantPayload(first),
            name: first.name.trim() || form.name.trim(),
          },
        });

        for (let i = 1; i < variants.length; i += 1) {
          const row = variants[i];
          await shopApi.createVariant(token, selectedShopId, created.id, {
            ...variantPayload(row),
            name: row.name.trim() || form.name.trim(),
          });
        }

        navigate(`/shop/products/${created.id}`, { replace: true });
        return;
      }

      const updated = await shopApi.updateProduct(token, selectedShopId, productId, {
        ...productPayload(form),
        default_variant_id: defaultVariantId,
      });
      setProduct(updated);
      setSuccess('Product saved.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const updateVariantRow = (localKey, patch) => {
    setVariants((rows) => rows.map((row) => (row.localKey === localKey ? { ...row, ...patch } : row)));
  };

  const persistVariant = async (localKey, patch = null) => {
    if (isNew || !product) return;
    const row = variants.find((v) => v.localKey === localKey);
    const current = patch ? { ...row, ...patch } : row;
    if (!current?.id || !current.price) return;

    setVariantBusy(localKey);
    setError(null);
    try {
      const token = await getToken();
      await shopApi.updateVariant(
        token,
        selectedShopId,
        product.id,
        current.id,
        variantPayload(current),
      );
      if (patch) {
        updateVariantRow(localKey, patch);
      }
      setSuccess('Variant updated.');
    } catch (err) {
      setError(err.message);
    } finally {
      setVariantBusy(null);
    }
  };

  const handleAddVariant = async () => {
    const currency = form.currency || 'USD';
    if (isNew) {
      setVariants((rows) => [...rows, emptyVariant(currency)]);
      return;
    }

    setVariantBusy('add');
    setError(null);
    try {
      const token = await getToken();
      const base = variants[variants.length - 1];
      const created = await shopApi.createVariant(token, selectedShopId, product.id, {
        name: `${form.name} — Variant ${variants.length + 1}`,
        sku: null,
        price: Number(base?.price || 0),
        cost_per_unit: Number(base?.cost_per_unit || base?.price || 0),
        currency: base?.currency || currency,
        track_inventory: true,
      });
      setVariants((rows) => [...rows, variantFromApi(created)]);
      setSuccess('Variant added.');
    } catch (err) {
      setError(err.message);
    } finally {
      setVariantBusy(null);
    }
  };

  const handleDeleteVariant = async (localKey) => {
    const row = variants.find((v) => v.localKey === localKey);
    if (!row) return;

    if (isNew) {
      if (variants.length <= 1) {
        setError('A product must have at least one variant.');
        return;
      }
      setVariants((rows) => rows.filter((v) => v.localKey !== localKey));
      return;
    }

    if (!window.confirm('Delete this variant? This cannot be undone.')) return;

    setVariantBusy(localKey);
    setError(null);
    try {
      const token = await getToken();
      await shopApi.deleteVariant(token, selectedShopId, product.id, row.id);
      await refreshProduct(token);
      setSuccess('Variant deleted.');
    } catch (err) {
      setError(err.message);
    } finally {
      setVariantBusy(null);
    }
  };

  const handleSetDefaultVariant = async (variantId) => {
    setDefaultVariantId(variantId);
    if (isNew || !product) return;

    setError(null);
    try {
      const token = await getToken();
      await shopApi.updateProduct(token, selectedShopId, product.id, {
        default_variant_id: variantId,
      });
      setSuccess('Default variant updated.');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleProductImageSelect = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || isNew || !product) return;

    setError(null);
    try {
      const token = await getToken();
      const image = await shopApi.uploadProductImage(token, selectedShopId, product.id, file);
      setImages((prev) => [...prev, image]);
      setSuccess('Image uploaded.');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRemoveProductImage = async (imageId) => {
    if (!product) return;
    setError(null);
    try {
      const token = await getToken();
      await shopApi.removeProductImage(token, selectedShopId, product.id, imageId);
      setImages((prev) => prev.filter((img) => img.id !== imageId));
      setSuccess('Image removed.');
    } catch (err) {
      setError(err.message);
    }
  };

  const openVariantImagePicker = (localKey) => {
    setPendingVariantImageKey(localKey);
    variantImageInputRef.current?.click();
  };

  const handleVariantImageSelect = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    const localKey = pendingVariantImageKey;
    setPendingVariantImageKey(null);
    if (!file || !localKey) return;

    const row = variants.find((v) => v.localKey === localKey);
    if (!row) return;

    if (isNew || !product || !row.id) {
      setError('Save the product before uploading variant images.');
      return;
    }

    setVariantBusy(localKey);
    setError(null);
    try {
      const token = await getToken();
      const updated = await shopApi.uploadVariantImage(
        token,
        selectedShopId,
        product.id,
        row.id,
        file,
      );
      updateVariantRow(localKey, {
        image_id: updated.image_id,
        imageUrl: mediaUrl(updated.image?.image, config.shopApiUrl),
      });
      await refreshProduct(token);
      setSuccess('Variant image uploaded.');
    } catch (err) {
      setError(err.message);
    } finally {
      setVariantBusy(null);
    }
  };

  const handleRemoveVariantImage = async (localKey) => {
    const row = variants.find((v) => v.localKey === localKey);
    if (!row?.id || !product) return;

    setVariantBusy(localKey);
    setError(null);
    try {
      const token = await getToken();
      const updated = await shopApi.removeVariantImage(token, selectedShopId, product.id, row.id);
      updateVariantRow(localKey, {
        image_id: updated.image_id,
        imageUrl: null,
      });
      setSuccess('Variant image removed.');
    } catch (err) {
      setError(err.message);
    } finally {
      setVariantBusy(null);
    }
  };

  if (loading) {
    return <LoadingState />;
  }

  if (!isNew && !product) {
    return <ErrorBanner message={error || 'Product not found.'} />;
  }

  const pageTitle = isNew ? 'Add product' : form.name || 'Edit product';

  return (
    <RequireShop>
      <div className="product-editor">
        <Breadcrumb
          items={[
            { label: 'Products', to: '/shop/products' },
            { label: pageTitle },
          ]}
        />

        <div className="product-editor-toolbar">
          <div>
            <h2 className="product-editor-title">{pageTitle}</h2>
            {!isNew && (
              <p className="product-editor-subtitle">
                {form.slug} · {variants.length} variant{variants.length === 1 ? '' : 's'}
              </p>
            )}
          </div>
          <div className="page-actions">
            <Link to="/shop/products" className="btn btn-secondary">
              Discard
            </Link>
            <button
              type="submit"
              form="product-editor-form"
              className="btn btn-primary"
              disabled={saving}
            >
              {saving ? 'Saving…' : isNew ? 'Save product' : 'Save'}
            </button>
          </div>
        </div>

        <ErrorBanner message={error} />
        {success && <div className="success-banner">{success}</div>}

        <form id="product-editor-form" onSubmit={handleSaveProduct}>
          <div className="product-editor-grid">
            <div className="product-editor-main">
              <SectionCard title="Title">
                <div className="form-field">
                  <input
                    id="product-title"
                    className="product-title-input"
                    placeholder="Short sleeve t-shirt"
                    value={form.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    required
                  />
                </div>
              </SectionCard>

              <SectionCard title="Description">
                <div className="form-field">
                  <label htmlFor="product-summary">Summary</label>
                  <input
                    id="product-summary"
                    placeholder="Short summary for listings"
                    value={form.summary}
                    onChange={(e) => setForm((p) => ({ ...p, summary: e.target.value }))}
                    maxLength={500}
                  />
                </div>
                <div className="form-field">
                  <label htmlFor="product-description">Description</label>
                  <textarea
                    id="product-description"
                    rows={6}
                    placeholder="Describe your product"
                    value={form.description}
                    onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                  />
                </div>
              </SectionCard>

              <SectionCard title="Media"
                description={
                  isNew
                    ? 'Save the product first, then upload images.'
                    : 'Product images shown on the storefront.'
                }
              >
                <input
                  ref={productImageInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  hidden
                  onChange={handleProductImageSelect}
                />
                <div className="media-grid">
                  {images.map((image) => (
                    <div key={image.id} className="media-thumb">
                      <img src={mediaUrl(image.image, config.shopApiUrl)} alt={image.image_alt_text} />
                      {!isNew && (
                        <button
                          type="button"
                          className="media-thumb-remove"
                          onClick={() => handleRemoveProductImage(image.id)}
                          aria-label="Remove image"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                  {!isNew && (
                    <button
                      type="button"
                      className="media-upload-btn"
                      onClick={() => productImageInputRef.current?.click()}
                    >
                      + Add image
                    </button>
                  )}
                </div>
              </SectionCard>

              <SectionCard
                title="Variants"
                description="Manage pricing, SKU, and images for each variant."
                actions={
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={handleAddVariant}
                    disabled={variantBusy === 'add'}
                  >
                    Add variant
                  </button>
                }
              >
                <input
                  ref={variantImageInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  hidden
                  onChange={handleVariantImageSelect}
                />
                <div className="variant-table-wrap">
                  <table className="variant-table">
                    <thead>
                      <tr>
                        <th>Image</th>
                        <th>Variant</th>
                        <th>SKU</th>
                        <th>Price</th>
                        <th>Cost</th>
                        <th>Inventory</th>
                        <th>Default</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {variants.map((row) => (
                        <tr key={row.localKey}>
                          <td>
                            <div className="variant-image-cell">
                              {row.imageUrl ? (
                                <div className="variant-image-preview">
                                  <img src={row.imageUrl} alt="" />
                                  {!isNew && row.id && (
                                    <button
                                      type="button"
                                      className="variant-image-remove"
                                      onClick={() => handleRemoveVariantImage(row.localKey)}
                                      disabled={variantBusy === row.localKey}
                                    >
                                      ×
                                    </button>
                                  )}
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  className="variant-image-upload"
                                  onClick={() => openVariantImagePicker(row.localKey)}
                                  disabled={isNew || !row.id || variantBusy === row.localKey}
                                  title={isNew ? 'Save product first' : 'Upload image'}
                                >
                                  +
                                </button>
                              )}
                            </div>
                          </td>
                          <td>
                            <input
                              className="variant-input"
                              value={row.name}
                              placeholder="e.g. Small / Red"
                              onChange={(e) => updateVariantRow(row.localKey, { name: e.target.value })}
                              onBlur={() => persistVariant(row.localKey)}
                            />
                          </td>
                          <td>
                            <input
                              className="variant-input"
                              value={row.sku}
                              onChange={(e) => updateVariantRow(row.localKey, { sku: e.target.value })}
                              onBlur={() => persistVariant(row.localKey)}
                            />
                          </td>
                          <td>
                            <input
                              className="variant-input variant-input-price"
                              type="number"
                              min="0"
                              step="0.01"
                              value={row.price}
                              required={isNew && variants[0].localKey === row.localKey}
                              onChange={(e) => updateVariantRow(row.localKey, { price: e.target.value })}
                              onBlur={() => persistVariant(row.localKey)}
                            />
                          </td>
                          <td>
                            <input
                              className="variant-input variant-input-price"
                              type="number"
                              min="0"
                              step="0.01"
                              value={row.cost_per_unit}
                              onChange={(e) =>
                                updateVariantRow(row.localKey, { cost_per_unit: e.target.value })
                              }
                              onBlur={() => persistVariant(row.localKey)}
                            />
                          </td>
                          <td>
                            <label className="checkbox-field variant-track">
                              <input
                                type="checkbox"
                                checked={row.track_inventory}
                                onChange={(e) => {
                                  const track_inventory = e.target.checked;
                                  updateVariantRow(row.localKey, { track_inventory });
                                  if (!isNew && row.id) {
                                    persistVariant(row.localKey, { track_inventory });
                                  }
                                }}
                              />
                              Track
                            </label>
                          </td>
                          <td>
                            {row.id ? (
                              <input
                                type="radio"
                                name="default-variant"
                                checked={defaultVariantId === row.id}
                                onChange={() => handleSetDefaultVariant(row.id)}
                              />
                            ) : (
                              '—'
                            )}
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              onClick={() => handleDeleteVariant(row.localKey)}
                              disabled={variantBusy === row.localKey || variants.length <= 1}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {!isNew && (
                  <p className="variant-hint">
                    Variant changes save when you leave a field. Product status saves with the Save button.
                  </p>
                )}
              </SectionCard>

              <SectionCard
                title="Search engine listing"
                description="Control how this product appears in search results."
              >
                <div className="form-field">
                  <label htmlFor="product-slug-seo">URL handle</label>
                  <input
                    id="product-slug-seo"
                    value={form.slug}
                    onChange={(e) => setForm((p) => ({ ...p, slug: e.target.value }))}
                    required
                  />
                </div>
                <div className="form-field">
                  <label htmlFor="product-meta-title">
                    Page title
                    <span className="field-hint">{form.meta_title.length}/800</span>
                  </label>
                  <input
                    id="product-meta-title"
                    value={form.meta_title}
                    onChange={(e) => setForm((p) => ({ ...p, meta_title: e.target.value }))}
                    maxLength={800}
                    placeholder={form.name || 'Product page title'}
                  />
                </div>
                <div className="form-field">
                  <label htmlFor="product-meta-description">
                    Meta description
                    <span className="field-hint">{form.meta_description.length}/350</span>
                  </label>
                  <textarea
                    id="product-meta-description"
                    rows={3}
                    value={form.meta_description}
                    onChange={(e) => setForm((p) => ({ ...p, meta_description: e.target.value }))}
                    maxLength={350}
                    placeholder="Brief description for search engines"
                  />
                </div>
                <SeoPreview
                  title={form.meta_title}
                  description={form.meta_description}
                  slug={form.slug}
                  productTitle={form.name}
                />
              </SectionCard>
            </div>

            <aside className="product-editor-sidebar">
              <SectionCard title="Status">
                <label className="checkbox-field">
                  <input
                    type="checkbox"
                    checked={form.is_live}
                    onChange={(e) => setForm((p) => ({ ...p, is_live: e.target.checked }))}
                  />
                  Visible on storefront
                </label>
                <p className="sidebar-hint">
                  {form.is_live ? 'This product is live.' : 'This product is a draft.'}
                </p>
              </SectionCard>

              <SectionCard title="Organization">
                <div className="form-grid">
                  <div className="form-field">
                    <label htmlFor="product-category">Category</label>
                    <select
                      id="product-category"
                      value={form.category_id}
                      onChange={(e) => setForm((p) => ({ ...p, category_id: e.target.value }))}
                      required
                    >
                      <option value="">Select category</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {isNew && (
                    <div className="form-field">
                      <label htmlFor="product-type">Product type</label>
                      <select
                        id="product-type"
                        value={form.product_type_id}
                        onChange={(e) => setForm((p) => ({ ...p, product_type_id: e.target.value }))}
                        required
                      >
                        <option value="">Select type</option>
                        {productTypes.map((type) => (
                          <option key={type.id} value={type.id}>
                            {type.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="form-field">
                    <label>Collections</label>
                    {collections.length === 0 ? (
                      <p className="sidebar-hint">
                        No collections yet.{' '}
                        <Link to="/shop/collections">Create a collection</Link>
                      </p>
                    ) : (
                      <div className="collection-checklist">
                        {collections.map((collection) => (
                          <label key={collection.id} className="checkbox-field collection-option">
                            <input
                              type="checkbox"
                              checked={form.collection_ids.includes(collection.id)}
                              onChange={() => toggleCollection(collection.id)}
                            />
                            {collection.name}
                          </label>
                        ))}
                      </div>
                    )}
                    <Link to="/shop/collections" className="sidebar-link">
                      Manage collections
                    </Link>
                  </div>
                  <div className="form-field">
                    <label>Tags</label>
                    <TagEditor
                      tags={form.tags}
                      onChange={(tags) => setForm((p) => ({ ...p, tags }))}
                      suggestions={allTags}
                    />
                  </div>
                  <div className="form-field">
                    <label htmlFor="product-brand">Brand</label>
                    <input
                      id="product-brand"
                      value={form.brand}
                      onChange={(e) => setForm((p) => ({ ...p, brand: e.target.value }))}
                    />
                  </div>
                  <div className="form-field">
                    <label htmlFor="product-currency">Currency</label>
                    <input
                      id="product-currency"
                      maxLength={3}
                      value={form.currency}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, currency: e.target.value.toUpperCase() }))
                      }
                    />
                  </div>
                </div>
              </SectionCard>

              {!isNew && product && (
                <SectionCard title="Summary">
                  <dl className="detail-meta">
                    <div>
                      <dt>Collections</dt>
                      <dd>
                        {product.collections?.length
                          ? product.collections.map((item) => item.name).join(', ')
                          : '—'}
                      </dd>
                    </div>
                    <div>
                      <dt>Tags</dt>
                      <dd>{product.tags?.length ? product.tags.map((item) => item.name).join(', ') : '—'}</dd>
                    </div>
                    <div>
                      <dt>Default price</dt>
                      <dd>
                        {(() => {
                          const v = variants.find((r) => r.id === defaultVariantId) || variants[0];
                          return v?.price ? formatMoney(v.price, v.currency || form.currency) : '—';
                        })()}
                      </dd>
                    </div>
                  </dl>
                  <Link to="/shop/inventory" className="btn btn-secondary btn-sm" style={{ marginTop: '0.75rem' }}>
                    Manage inventory
                  </Link>
                </SectionCard>
              )}
            </aside>
          </div>
        </form>
      </div>
    </RequireShop>
  );
}
